import { ColumnState, GridApi } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useReportErrorsCallback } from '@/lib/connect/error-reporting'
import {
  getFilterSourceParams,
  getStoredSourceParams,
  writeStoredFilterParam,
  writeStoredSort,
} from '@/lib/storage/local-storage.hook'
import type { ReplaceParamsOptions } from '@/lib/url/base-url-param.hook'

import { CANONICAL_PARAM_KEYS } from '@/components/lemmala-design-system/table/consts'
import type { FilterModel, ViewManager } from '@/components/lemmala-design-system/table/types'
import { applySortModelViaColumnState } from '@/components/lemmala-design-system/table/utils/grid-state'
import {
  urlParamToColumnState as defaultUrlParamToColumnState,
  urlParamToSortModel as defaultUrlParamToSortModel,
  urlToFilterModel as defaultUrlToFilterModel,
  toDefinedParams,
} from '@/components/lemmala-design-system/table/utils/url-serializers'

/**
 * Configuration for view management functionality.
 */
export interface ViewManagerConfig {
  /**
   * Function to get view definition by name.
   * Should return an object with filterModel, columnState, and filterBarFilters.
   */
  getViewDefinition: (viewName: string) => {
    label?: string
    filterModel?: FilterModel
    columnState: ColumnState[]
    filterBarFilters?: string[]
    [key: string]: unknown
  }

  /**
   * Array of available view names/options.
   */
  viewOptions: string[]

  /**
   * URL parameter management functions.
   */
  urlManager: {
    /** Current URL parameters */
    urlParams: Record<string, string>
    /** Function to update URL parameters */
    replaceParams: (updates: ReplaceParamsOptions) => void
  }

  /**
   * URL serialization functions for filters and sort models.
   */
  urlSerializers?: {
    /** Convert URL params to filter model */
    urlToFilterModel?: (params: Record<string, string | undefined>) => FilterModel
    /** Convert URL param to sort model */
    urlToSortModel?: (sortParam: string) => Array<{ colId: string; sort: 'asc' | 'desc' }>
    /** Convert URL param to column state */
    urlParamToColumnState?: (
      urlParam: string | null | undefined,
      allColumnIds: string[],
    ) => Partial<ColumnState>[] | null
  }

  /**
   * URL parameter names/keys to use.
   */
  urlParamKeys?: {
    view?: string
    filter?: string
    risk?: string
    status?: string
    sort?: string
    columns?: string
    [key: string]: string | undefined
  }

  /**
   * All available column IDs for column state restoration.
   * Required for column customization to work properly with views.
   */
  allColumnIds?: string[]

  /**
   * Default view name to use when no view is specified.
   * @default "All"
   */
  defaultView?: string

  /**
   * Callback to notify when the current view changes.
   */
  onViewChange?: (viewName: string) => void

  /**
   * When true, each view keeps its own filters instead of carrying non-status filters across
   * view changes.
   */
  perViewFilters?: boolean

  /**
   * The user's persisted column layout, applied instead of the view defaults when present.
   */
  getStoredColumnState?: () => ColumnState[] | null

  /**
   * `{userId}_{tenantId}:{tablePrefix}` key for persisted table state (composed by
   * AgDataTable). Undefined disables persistence.
   */
  filterStorageKey?: string
}

/**
 * Generic view management hook for AG Grid tables.
 *
 * This hook provides the same view management functionality as the original useViewManagement
 * but with configurable view definitions, URL parameter handling, and serialization functions.
 *
 * Features:
 * - Configurable view definitions and options
 * - URL parameter synchronization with configurable parameter names
 * - Column state and filter model application per view
 * - View restoration from URL on mount
 * - Conflict prevention with URL synchronization during view changes
 *
 * @example
 * ```tsx
 * const viewManager = useAgViewManager(gridApi, {
 *   getViewDefinition: (name) => inventoryViewDefinitions[name],
 *   viewOptions: ['All', 'Active', 'Pending'],
 *   urlManager: {
 *     urlParams: useBaseUrlParams().urlParams,
 *     replaceParams: useBaseUrlParams().replaceParams,
 *   },
 *   urlSerializers: {
 *     urlToFilterModel: urlParamsToFilterModel,
 *     urlToSortModel: urlParamToSortModel,
 *   },
 *   urlParamKeys: {
 *     view: 'VIEW',
 *     filter: 'FILTER',
 *     sort: 'SORT',
 *   },
 * })
 * ```
 */
export const useAgViewManager = <TRowData = unknown>(
  gridApi: GridApi<TRowData> | null,
  config: ViewManagerConfig,
): ViewManager => {
  const {
    getViewDefinition,
    viewOptions,
    urlManager,
    urlSerializers,
    urlParamKeys = {},
    defaultView = 'All',
    allColumnIds = [],
    onViewChange,
    perViewFilters = false,
    getStoredColumnState,
    filterStorageKey,
  } = config

  const { urlParams, replaceParams } = urlManager
  const [currentView, setCurrentView] = useState(defaultView)
  const reportErrors = useReportErrorsCallback()
  const restoredRef = useRef(false)
  const initializedViewRef = useRef(false)
  const isApplyingViewRef = useRef(false)
  // Tracks the active view without waiting for the currentView state to settle, and the
  // per-view non-status filters (used only when perViewFilters is on).
  const currentViewRef = useRef(defaultView)
  const viewFiltersRef = useRef<Record<string, FilterModel>>({})

  // Helper to apply column state while preserving checkbox column
  const applyColumnState = useCallback((gridApi: GridApi, newColumnState: ColumnState[]) => {
    // Get current column state to preserve checkbox column
    const checkboxColumn = gridApi
      .getColumnState()
      .find((col) => col.colId === 'ag-Grid-SelectionColumn')

    // No custom column state, use view's default (only if view has column state defined)
    gridApi.applyColumnState({
      defaultState: { hide: true },
      state: checkboxColumn ? [checkboxColumn, ...newColumnState] : newColumnState,
      applyOrder: true,
    })
  }, [])

  const applyViewColumnState = useCallback(
    (gridApi: GridApi, viewColumnState: ColumnState[]) => {
      const persisted = getStoredColumnState?.()
      if (persisted?.length) {
        gridApi.applyColumnState({ state: persisted, applyOrder: true })
        return
      }
      applyColumnState(gridApi, viewColumnState)
    },
    [getStoredColumnState, applyColumnState],
  )

  // Default URL parameter keys
  const paramKeys = useMemo(
    () => ({
      view: urlParamKeys.view || CANONICAL_PARAM_KEYS.view,
      filter: urlParamKeys.filter || CANONICAL_PARAM_KEYS.filter,
      sort: urlParamKeys.sort || CANONICAL_PARAM_KEYS.sort,
      columns: urlParamKeys.columns || CANONICAL_PARAM_KEYS.columns,
      // legacy keys omitted on purpose
      ...urlParamKeys,
    }),
    [urlParamKeys],
  )

  // Provide default serializers for restoration when not supplied
  const serializers = useMemo(
    () => ({
      urlToFilterModel:
        urlSerializers?.urlToFilterModel ||
        ((p: Record<string, string | undefined>): FilterModel =>
          defaultUrlToFilterModel(toDefinedParams(p), {
            filter: paramKeys.filter!,
          })),
      urlToSortModel:
        urlSerializers?.urlToSortModel || ((raw: string) => defaultUrlParamToSortModel(raw)),
      urlParamToColumnState: urlSerializers?.urlParamToColumnState || defaultUrlParamToColumnState,
    }),
    [urlSerializers, paramKeys],
  )

  const applyView = useCallback(
    (viewName: string, preserveExistingFilters = false, clearUrlFilters = false) => {
      isApplyingViewRef.current = true
      const view = getViewDefinition(viewName)

      // Null safety - return early if view definition is invalid
      if (!view) {
        isApplyingViewRef.current = false
        return
      }

      const previousView = currentViewRef.current
      currentViewRef.current = viewName
      setCurrentView(viewName)
      onViewChange?.(viewName)

      // Set or clear the VIEW parameter in URL
      if (viewName === defaultView) {
        // For default view, clear the VIEW param and other filters if requested
        const removeParams = [paramKeys.view]
        if (clearUrlFilters) removeParams.push(paramKeys.filter)
        replaceParams({
          remove: removeParams.filter(Boolean) as string[],
          replaceCurrentHistoryEntry: true,
        })
      } else {
        // For other views, set the VIEW param and clear filter params if requested
        const updateParams: Array<[string, string]> = [[paramKeys.view, viewName]]
        const removeParams: string[] = []

        if (clearUrlFilters) removeParams.push(paramKeys.filter)

        replaceParams({
          update: updateParams,
          remove: removeParams,
          replaceCurrentHistoryEntry: true,
        })
      }

      if (clearUrlFilters) {
        writeStoredFilterParam(filterStorageKey, null)
      }

      if (!gridApi) {
        // Grid not ready yet; URL + state updated above, grid changes will happen via effects
        return
      }

      // Apply column customization from URL if present
      const columnParam = paramKeys.columns ? urlParams[paramKeys.columns] : undefined

      if (columnParam && allColumnIds.length > 0) {
        // If we have custom column state, use it instead of the view's default
        try {
          const columnStateParam = decodeURIComponent(columnParam)
          const customColumnState = serializers.urlParamToColumnState(
            columnStateParam,
            allColumnIds,
          )
          if (customColumnState) {
            // Apply custom column state directly (replaces view's column config)
            applyColumnState(gridApi, customColumnState as ColumnState[])
          }
        } catch (e) {
          reportErrors(
            e instanceof Error
              ? e
              : new Error('[AgViewManager] Failed to apply column customization'),
          )
        }
      } else if (view.columnState && view.columnState.length > 0) {
        applyViewColumnState(gridApi, view.columnState)
      }
      // If no column state from URL or view, don't apply any column state
      // (let AG Grid use the columnDefs as-is)

      // Apply filter model
      // Strategy: Always preserve non-status filters (e.g., "Scope") regardless of preserveExistingFilters.
      // The preserveExistingFilters flag controls whether to preserve the status filter or use the view's status.
      const viewFilterModel = (view.filterModel as FilterModel) || {}
      const currentFilterModel = gridApi.getFilterModel() || {}

      // Extract non-status filters (these always persist across view changes)
      const { status: _currentStatus, ...nonStatusFilters } = currentFilterModel

      // Build merged filter model
      let mergedFilterModel: FilterModel
      if (preserveExistingFilters) {
        // Preserve all current filters (including status), then apply view filters (view overrides)
        mergedFilterModel = { ...currentFilterModel, ...viewFilterModel }
      } else if (perViewFilters) {
        // Stash the leaving view's filters and restore the entered view's own.
        viewFiltersRef.current[previousView] = nonStatusFilters
        mergedFilterModel = { ...(viewFiltersRef.current[viewName] ?? {}), ...viewFilterModel }
      } else {
        // Preserve non-status filters, but use view's status filter
        mergedFilterModel = { ...nonStatusFilters, ...viewFilterModel }
      }

      gridApi.setFilterModel(mergedFilterModel)

      // Refresh filtering after state changes
      gridApi.onFilterChanged()

      // Clear the flag after a short delay to allow filter sync to complete
      setTimeout(() => {
        isApplyingViewRef.current = false
      }, 100)
    },
    [
      getViewDefinition,
      onViewChange,
      defaultView,
      gridApi,
      paramKeys.columns,
      paramKeys.view,
      paramKeys.filter,
      urlParams,
      allColumnIds,
      replaceParams,
      serializers,
      applyColumnState,
      applyViewColumnState,
      reportErrors,
      perViewFilters,
      filterStorageKey,
    ],
  )

  // Restore view from URL on initialization (run once)
  useEffect(() => {
    if (initializedViewRef.current) return
    const viewFromUrl = urlParams[paramKeys.view]
    if (viewFromUrl && viewOptions.includes(viewFromUrl)) {
      currentViewRef.current = viewFromUrl
      setCurrentView(viewFromUrl)
      onViewChange?.(viewFromUrl)
    }
    initializedViewRef.current = true
  }, [urlParams, paramKeys.view, viewOptions, onViewChange])

  // Apply view and filters when gridApi becomes available (run once only)
  useEffect(() => {
    // Only run this effect once when gridApi first becomes available
    if (!gridApi || restoredRef.current) return

    const viewFromUrl = urlParams[paramKeys.view]

    // URL filter wins; fall back to localStorage when navigating back without query params
    const { resolved, params: filterSourceParams } = getFilterSourceParams(
      filterStorageKey,
      paramKeys.filter,
      urlParams,
    )
    const hasFilterParams = !!resolved
    const { resolved: resolvedSort } = getStoredSourceParams(
      filterStorageKey,
      paramKeys.sort,
      urlParams,
      'sort',
    )

    const syncResolvedFilter = () => {
      const update: Array<[string, string]> = []
      if (paramKeys.filter && resolved) {
        if (!urlParams[paramKeys.filter]) update.push([paramKeys.filter, resolved])
        else writeStoredFilterParam(filterStorageKey, resolved)
      }
      if (paramKeys.sort && resolvedSort) {
        if (!urlParams[paramKeys.sort]) update.push([paramKeys.sort, resolvedSort])
        else writeStoredSort(filterStorageKey, resolvedSort)
      }
      if (update.length > 0) {
        replaceParams({ update, replaceCurrentHistoryEntry: true })
      }
    }

    if (viewFromUrl && viewOptions.includes(viewFromUrl)) {
      // If we have filters in the URL (or stored), preserve them for all views (including default)
      if (hasFilterParams) {
        try {
          const urlFilters = serializers.urlToFilterModel(filterSourceParams)

          // Saved/URL filters win. View defaults only apply when storage/URL is empty
          // (see the applyView branch below) so returning users keep their choices.
          const view = getViewDefinition(viewFromUrl)

          // Check for column customization first
          const columnParam = paramKeys.columns ? urlParams[paramKeys.columns] : undefined

          if (columnParam && allColumnIds.length > 0) {
            // Apply custom column state (replaces view's default)
            try {
              const columnStateParam = decodeURIComponent(columnParam)
              const customColumnState = serializers.urlParamToColumnState(
                columnStateParam,
                allColumnIds,
              )
              if (customColumnState) {
                applyColumnState(gridApi, customColumnState as ColumnState[])
              }
            } catch (e) {
              reportErrors(
                e instanceof Error
                  ? e
                  : new Error('[AgViewManager] Failed to restore column customization from URL'),
              )
            }
          } else if (view.columnState && view.columnState.length > 0) {
            applyViewColumnState(gridApi, view.columnState)
          }

          // Apply filters and sort after a minimal delay to ensure column state is fully processed
          setTimeout(() => {
            gridApi.setFilterModel(urlFilters)
            gridApi.onFilterChanged()
            syncResolvedFilter()

            // Also restore sort from URL if present
            const sortParam = resolvedSort
            if (sortParam) {
              try {
                const sortModel = serializers.urlToSortModel(sortParam)
                applySortModelViaColumnState(gridApi, sortModel)
              } catch (e) {
                reportErrors(
                  e instanceof Error
                    ? e
                    : new Error('[AgViewManager] Failed to restore sort from URL'),
                )
              }
            }
          }, 50) // 50ms delay - reduced from 100ms for better performance
          restoredRef.current = true
        } catch (e) {
          reportErrors(
            e instanceof Error
              ? e
              : new Error(
                  '[AgViewManager] Failed to parse filter from URL during view restoration',
                ),
          )
          // Fallback to normal view application
          applyView(viewFromUrl)
          restoredRef.current = true
        }
      } else {
        // No filters in URL, just apply the view normally
        applyView(viewFromUrl)
        restoredRef.current = true
      }
    } else if (hasFilterParams) {
      // No view specified in URL, but filters exist - apply filters to default view
      try {
        const urlFilters = serializers.urlToFilterModel(filterSourceParams)

        // Saved/URL filters win; default-view filterModel is only for empty storage.
        const defaultViewDef = getViewDefinition(defaultView)

        // Check for column customization first
        const columnParam = paramKeys.columns ? urlParams[paramKeys.columns] : undefined
        if (columnParam && allColumnIds.length > 0) {
          // Apply custom column state (replaces view's default)
          try {
            const columnStateParam = decodeURIComponent(columnParam)
            const customColumnState = serializers.urlParamToColumnState(
              columnStateParam,
              allColumnIds,
            )
            if (customColumnState) {
              applyColumnState(gridApi, customColumnState as ColumnState[])
            }
          } catch (e) {
            reportErrors(
              e instanceof Error
                ? e
                : new Error('[AgViewManager] Failed to restore column customization from URL'),
            )
          }
        } else if (defaultViewDef.columnState && defaultViewDef.columnState.length > 0) {
          applyViewColumnState(gridApi, defaultViewDef.columnState)
        }

        // Apply filters and sort after a minimal delay
        setTimeout(() => {
          gridApi.setFilterModel(urlFilters)
          gridApi.onFilterChanged()
          syncResolvedFilter()

          // Also restore sort from URL if present
          const sortParam = resolvedSort
          if (sortParam) {
            try {
              const sortModel = serializers.urlToSortModel(sortParam)
              applySortModelViaColumnState(gridApi, sortModel)
            } catch (e) {
              reportErrors(
                e instanceof Error
                  ? e
                  : new Error('[AgViewManager] Failed to restore sort from URL'),
              )
            }
          }
        }, 50)
        restoredRef.current = true
      } catch (e) {
        reportErrors(
          e instanceof Error
            ? e
            : new Error('[AgViewManager] Failed to parse filter from URL when no view specified'),
        )
        restoredRef.current = true
      }
    } else {
      // No view in URL and no filter params - apply default view with any column customization
      const columnParam = paramKeys.columns ? urlParams[paramKeys.columns] : undefined
      if (columnParam && allColumnIds.length > 0) {
        // Apply custom column state (replaces view's default)
        try {
          const columnStateParam = decodeURIComponent(columnParam)
          const customColumnState = serializers.urlParamToColumnState(
            columnStateParam,
            allColumnIds,
          )
          if (customColumnState) {
            applyColumnState(gridApi, customColumnState as ColumnState[])
          }
        } catch (e) {
          reportErrors(
            e instanceof Error
              ? e
              : new Error('[AgViewManager] Failed to restore column customization from URL'),
          )
        }

        restoredRef.current = true
      } else {
        // Check if default view has column state to apply
        const defaultViewDef = getViewDefinition(defaultView)
        if (defaultViewDef.columnState && defaultViewDef.columnState.length > 0) {
          // Apply the default view only if it has column state defined
          applyView(defaultView)
        }
        restoredRef.current = true
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi])

  return {
    currentView,
    applyView,
    isApplyingViewRef,
    viewOptions,
  }
}
